import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import DemoDialog from "./DemoDialog.component";

interface IDemoDialogContext {
  /** Opens the demo request dialog, optionally pre-filled with a plan name. */
  openDemoDialog: (plan?: string) => void;
}

const DemoDialogContext = createContext<IDemoDialogContext>({
  openDemoDialog: () => undefined,
});

export const useDemoDialog = () => useContext(DemoDialogContext);

interface IDemoDialogProvider {
  children: React.ReactNode;
}

export const DemoDialogProvider = ({ children }: IDemoDialogProvider) => {
  const [plan, setPlan] = useState<string | undefined>(undefined);
  const [isOpen, setIsOpen] = useState(false);
  const [trigger, setTrigger] = useState<HTMLElement | null>(null);

  const openDemoDialog = useCallback((requestedPlan?: string) => {
    setTrigger(document.activeElement as HTMLElement);
    setPlan(requestedPlan);
    setIsOpen(true);
  }, []);

  const closeDemoDialog = useCallback(() => {
    setIsOpen(false);
    if (trigger && typeof trigger.focus === "function") {
      trigger.focus();
    }
  }, [trigger]);

  const value = useMemo(() => ({ openDemoDialog }), [openDemoDialog]);

  return (
    <DemoDialogContext.Provider value={value}>
      {children}
      <DemoDialog isOpen={isOpen} plan={plan} onClose={closeDemoDialog} />
    </DemoDialogContext.Provider>
  );
};

export default DemoDialogContext;
